// Time & billing (F02): hours and amounts per engagement (`time.firm`, partners), each engagement's budget against actual by phase
// and hours by person (`time.report`, the engagement's `time_summary`), and the acts: log time (a person's own act), budgets,
// rates (partner), draft an invoice (manager) and issue it (partner). Figures and budget states are the engine's.
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Card, ErrorState, LoadingRows, PageHeader, StatusPill } from "../../components";
import type { DocModel, EngagementHeader } from "../../data/types";
import { useLoad } from "../../lib/hooks";
import { ActButton, Computed, ModelGate, useActs, useFirmModels } from "./common";
import { Table } from "./Registers";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export function TimePage() {
  const { ds, me } = useApp();
  const ms = useFirmModels(["time_firm"]);
  const es = useLoad(async () => {
    const list = await ds.engagements();
    return Promise.all(list.map(async (e) => ({ e, t: await ds.table(e.id, "time_summary").then((r) => r[0] ?? null, (x: Error) => ({ error: x.message }) as { error: string }) })));
  }, [ds]);
  const [act, drawer] = useActs();
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Time & billing" subtitle="Hours and budgets per engagement, rates and invoices. Budget states and amounts are the engine's."
        actions={<ActButton command="time.rate" label="Set a rate…" onClick={() => act({ command: "time.rate", title: "Set a billing rate" })} />} />
      {ms.data && (me.role === "partner" ? (
        <Card title="Across the firm" action={<Computed m={ms.data.time_firm} />}>
          <ModelGate<Doc> kind="time_firm" m={ms.data.time_firm} label="Firm time">
            {(d) => <Table rows={d.engagements ?? []} empty="No time recorded." cols={[
              ["Engagement", (r) => r.engagement], ["Hours", (r) => <span className="tabular-nums">{r.hours}</span>], ["Amount", (r) => <span className="tabular-nums">{r.amount}</span>],
              ["Unbilled", (r) => <span className="tabular-nums">{r.unbilled_hours} h · {r.unbilled_amount}</span>],
              ["Over budget", (r) => ((r.over_budget ?? []).length ? <StatusPill state="blocked" label={(r.over_budget as string[]).join(", ")} /> : <StatusPill state="done" label="Within budget" />)],
              ["No rate", (r) => (r.no_rate ? <StatusPill state="missing" label={`${r.no_rate} entries`} /> : "—")],
            ]} rowTone={(r) => ((r.over_budget ?? []).length ? "waiting" : undefined)} />}
          </ModelGate>
        </Card>
      ) : <p className="rounded-md border border-dashed border-line px-3 py-2 text-sm text-muted">Firm-wide amounts are for partners; below are the engagements you work on.</p>)}
      {es.error ? <ErrorState error={es.error} onRetry={es.reload} /> : !es.data ? <LoadingRows rows={3} /> : es.data.map(({ e, t }) => <EngagementTime key={e.id} e={e} t={t} act={act} />)}
      <p className="text-xs text-muted">Individual time entries and invoices are not published yet; the engine lists them with <span className="font-mono">yvc time sheet</span> and <span className="font-mono">yvc time report</span>.</p>
      {drawer}
    </div>
  );
}

function EngagementTime({ e, t, act }: { e: EngagementHeader; t: DocModel | { error: string } | null; act: ReturnType<typeof useActs>[0] }) {
  const doc = t && "doc" in t ? (t.doc as Doc | null) : null;
  const err = t && "error" in t && t.error ? t.error : null;
  return (
    <Card title={<Link className="hover:underline" to={`/e/${e.id}`}>{e.clientName}</Link>} action={
      <span className="flex flex-wrap gap-2">
        <ActButton scope="engagement" command="time.log" label="Log my time…" onClick={() => act({ command: "time.log", engagementId: e.id, title: `Log your time on ${e.clientName}` })} />
        <ActButton scope="engagement" command="time.budget" label="Set a budget…" onClick={() => act({ command: "time.budget", engagementId: e.id, title: `Budget for ${e.clientName}` })} />
        <ActButton scope="engagement" command="invoice.draft" label="Draft an invoice…" onClick={() => act({ command: "invoice.draft", engagementId: e.id, title: `Draft an invoice for ${e.clientName}` })} />
        <ActButton scope="engagement" command="invoice.issue" label="Issue an invoice…" tone="primary" onClick={() => act({ command: "invoice.issue", engagementId: e.id, title: `Issue an invoice for ${e.clientName}` })} />
      </span>
    }>
      {err ? <ErrorState error={`The engine could not compute the time report: ${err}`} /> : !doc ? <p className="text-sm text-muted">No time report published for this engagement.</p> : (
        <div className="space-y-3 text-sm">
          <p><span className="tabular-nums font-semibold">{doc.hours}</span> h · amount <span className="tabular-nums">{doc.amount}</span> · unbilled <span className="tabular-nums">{doc.unbilled_hours} h ({doc.unbilled_amount})</span></p>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Budget against actual</h3>
              {(doc.by_phase ?? []).length === 0 ? <p className="text-muted">No time or budget by phase yet.</p> : (
                <ul className="space-y-2">
                  {(doc.by_phase as Doc[]).map((p) => (
                    <li key={p.phase}>
                      <span className="flex items-center justify-between gap-2"><span className="capitalize">{p.phase}</span>
                        <span className="flex items-center gap-2 text-xs"><span className="tabular-nums">{p.hours}{p.budget ? ` of ${p.budget}` : ""} h</span>{p.state && <StatusPill state={p.state === "over budget" ? "blocked" : "done"} label={p.state} />}</span></span>
                      {p.budget && p.used_pct !== undefined && (
                        <span role="meter" aria-label={`${p.phase}: ${p.used_pct}% of budget used`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, Number(p.used_pct))} className="mt-1 block h-1.5 overflow-hidden rounded bg-sunken ring-1 ring-line">
                          <span className={`block h-full ${Number(p.used_pct) > 100 ? "bg-blocked" : "bg-done"}`} style={{ width: `${Math.min(100, Number(p.used_pct))}%` }} />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">By person</h3>
              {(doc.by_person ?? []).length === 0 ? <p className="text-muted">No time logged.</p> : <ul className="divide-y divide-line">{(doc.by_person as Doc[]).map((p) => <li key={p.person} className="flex justify-between py-1"><span>{p.person}</span><span className="tabular-nums">{p.hours} h{p.amount ? ` · ${p.amount}` : ""}</span></li>)}</ul>}
              {(doc.no_rate ?? []).length > 0 && <p className="mt-1 text-xs text-blocked">No rate for: {(doc.no_rate as string[]).join(", ")}</p>}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
